import { useServices } from './services-registry';
import { useCollapseExpand } from './collapse-expand';
import { loadingScheduler } from '../v1/services/loading-scheduler';
import { Message } from '@wix/chat-sdk';
import { useAppState } from './app-state';
import { triggerInteractivity } from '../init/chat-loader';
import { SocialServerApi } from '../services/social-server-api';
import { DeviceType } from '../types/host-sdk';

export enum CorvidReportEvent {
  ChatIsReady = 'ChatWidget.isReady',
  MessageReceived = 'ChatWidget.onMessageReceived',
  MessageSent = 'ChatWidget.onMessageSent',
  WidgetCollapsed = 'ChatWidget.onWidgetCollapsed',
  WidgetExpand = 'ChatWidget.onWidgetExpand',
}

export enum CorvidCommand {
  SendMessage = 'ChatWidget.sendMessage',
  ExpandWidget = 'ChatWidget.expandWidget',
  CollapseWidget = 'ChatWidget.collapseWidget',
  GetChatState = 'ChatWidget.getChatState',
  StartPrivateChat = 'ChatWidget.startPrivateChat',
}

export const useCorvidApi = () => {
  const { hostSdk } = useServices();

  const reportMessageReceived = (message: any) =>
    hostSdk.publishToPubSub(CorvidReportEvent.MessageReceived, message);
  const reportMessageSent = (message: any) =>
    hostSdk.publishToPubSub(CorvidReportEvent.MessageSent, message);

  const reportWidgetCollapsed = () =>
    hostSdk.publishToPubSub(CorvidReportEvent.WidgetCollapsed);
  const reportWidgetExpand = () =>
    hostSdk.publishToPubSub(CorvidReportEvent.WidgetExpand);

  return {
    reportMessageReceived,
    reportMessageSent,
    reportWidgetCollapsed,
    reportWidgetExpand,
    // focusChannel,
    // getChannelList,
    // maximized,
    // getChannel,
    // startChannel,
  };
};

let ce; // FIXME temp solution registerCorvidApi should be called once and still make collapseExpand update

export const useCorvidCommandApi = () => {
  const { hostSdk, axiosInstance } = useServices();
  const collapseExpand = useCollapseExpand();
  const { appState, updateAppState } = useAppState();
  ce = collapseExpand;

  const respondToEvent = (
    viewerSubscribeResponseEvent: CorvidCommand,
    requestId: string | null,
    success: boolean = true,
    results?: object | null,
  ) => {
    if (requestId) {
      const responseEvent = `${viewerSubscribeResponseEvent}Response.${requestId}`;
      hostSdk.publishToPubSub(
        responseEvent,
        results ? { success, results } : { success },
      );
    }
  };

  const handleSendMessageRequest = async (options: any = {}) => {
    const {
      message,
      chatroom,
      requestId,
    }: {
      message: string;
      chatroom?: string;
      requestId: string;
    } = options.data || {};

    if (!message) {
      console.error('`message` must be populated when sending message');
      return respondToEvent(CorvidCommand.SendMessage, requestId, false);
    }

    try {
      const defaultChatroomId = appState.businessChatroomId ?? '';
      const userId = appState.visitorId;
      const createdAt = new Date().getDate();
      const messageParam = {
        createdAt,
        summary: message,
        chatroomId: chatroom ? chatroom : defaultChatroomId,
        sender: {
          type: 'Visitor',
          userId,
        },
        text: message,
        data: [{ data: { text: message } }],
      };

      const {
        chatSdk,
        Message: { fromText },
      } = await import(
        /* webpackChunkName: "expanded-widget" */ '@wix/chat-sdk'
      );
      const messageObj: Message = fromText(messageParam);
      triggerInteractivity();
      await loadingScheduler.onConnective();

      chatSdk.sendMessage(messageObj);
      respondToEvent(CorvidCommand.SendMessage, requestId);
    } catch (e) {
      return respondToEvent(CorvidCommand.SendMessage, requestId, false);
    }
  };

  const handleCollapseWidgetRequest = async (options: any = {}) => {
    await ce.collapse();
    respondToEvent(CorvidCommand.CollapseWidget, options.data?.requestId);
  };

  const handleExpandWidgetRequest = async (options: any = {}) => {
    await ce.expand();
    respondToEvent(CorvidCommand.ExpandWidget, options.data?.requestId);
  };

  const handleGetChatStateRequest = (options: any = {}) =>
    respondToEvent(CorvidCommand.GetChatState, options.data?.requestId, true, {
      isWidgetExpanded: ce.isExpanded,
    });

  const handleStartPrivateChatRequest = async (options: any = {}) => {
    if (options.data?.userId) {
      updateAppState({ activeSocialMemberId: options.data?.userId });
      collapseExpand.expand();
    }

    respondToEvent(CorvidCommand.CollapseWidget, options.data?.requestId);
  };

  const registerCorvidApi = () => {
    // maximize
    hostSdk.subscribeToPubSub(
      CorvidCommand.ExpandWidget,
      handleExpandWidgetRequest,
      true,
    );
    // minimize
    hostSdk.subscribeToPubSub(
      CorvidCommand.CollapseWidget,
      handleCollapseWidgetRequest,
    );
    // maximized/...
    hostSdk.subscribeToPubSub(
      CorvidCommand.GetChatState,
      handleGetChatStateRequest,
    );
    // sendMessage
    hostSdk.subscribeToPubSub(
      CorvidCommand.SendMessage,
      handleSendMessageRequest,
    );
    // startPrivateChat
    hostSdk.subscribeToPubSub(
      CorvidCommand.StartPrivateChat,
      handleStartPrivateChatRequest,
    );

    hostSdk.publishToPubSub(CorvidReportEvent.ChatIsReady);
  };

  return {
    registerCorvidApi,
  };
};
